import React, { memo } from 'react'
import { Document, Page, Text, View, StyleSheet, Font } from '@react-pdf/renderer'
import { createMarkdownInstance } from './config/markdownConfig'
import type { MarkdownRendererProps } from './types'
import { convertMermaidToPdfSvg, createMermaidPlaceholder } from '@/utils/mermaidToPdfSvg'

// Register emoji source for better emoji rendering
Font.registerEmojiSource({
  format: 'png',
  url: 'https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/72x72/'
});

// Font family mapping (map user-selected fonts to PDF standard fonts)
const fontFamilyMap = {
  'Times New Roman': 'Times-Roman',
  'Arial': 'Helvetica',
  'Helvetica': 'Helvetica',
  'Courier': 'Courier',
  'Calibri': 'Helvetica' // Fallback for Calibri
};

// Helper for bold/italic font family
const getBoldFont = (fontFamily: string) => {
  if (fontFamily === 'Times-Roman') return 'Times-Bold';
  if (fontFamily === 'Helvetica') return 'Helvetica-Bold';
  if (fontFamily === 'Courier') return 'Courier-Bold';
  return fontFamily;
};

const getItalicFont = (fontFamily: string) => {
  if (fontFamily === 'Times-Roman') return 'Times-Italic';
  if (fontFamily === 'Helvetica') return 'Helvetica-Oblique';
  if (fontFamily === 'Courier') return 'Courier-Oblique';
  return fontFamily;
};

interface PDFStyleSettings {
  fontFamily?: string;
  fontSize?: string;
  lineSpacing?: string;
  marginTop?: string;
  marginBottom?: string;
  marginLeft?: string;
  marginRight?: string;
  pageSize?: string;
  orientation?: string;
}

interface MarkdownRendererToPdfProps extends Omit<MarkdownRendererProps, 'onSectionSelect' | 'sectionStates' | 'selectedSection'> {
  styleSettings?: PDFStyleSettings;
  title?: string;
  headerTemplate?: string;
  footerTemplate?: string;
  disableMermaid?: boolean; // Add flag to disable Mermaid if causing issues
}

// Define styles
const createStyleSheet = (settings: PDFStyleSettings = {}) => {
  // Map the selected font to a standard PDF font
  const fontFamily = fontFamilyMap[settings?.fontFamily || 'Times New Roman'] || 'Times-Roman';
  const fontSize = parseInt(settings?.fontSize?.replace('pt', '') || '12') || 12;
  const lineHeight = parseFloat(settings?.lineSpacing || '3.5') || 3.5;
  
  return StyleSheet.create({
    page: {
      flexDirection: 'column',
      backgroundColor: '#fff',
      padding: '10mm',
      fontFamily,
      fontSize,
      lineHeight
    },
    section: {
      margin: 10,
      padding: 10,
      flexGrow: 1
    },
    header: {
      marginBottom: '10mm',
      borderBottom: '1px solid #eaeaea',
      paddingBottom: '5mm'
    },
    footer: {
      marginTop: '10mm',
      borderTop: '1px solid #eaeaea',
      paddingTop: '5mm',
      textAlign: 'center',
      fontSize: 10,
      color: '#666'
    },
    heading1: {
      fontSize: Math.round(fontSize * 1.5),
      fontFamily: getBoldFont(fontFamily),
      marginBottom: 12,
      marginTop: 20
    },
    heading2: {
      fontSize: Math.round(fontSize * 1.3),
      fontFamily: getBoldFont(fontFamily),
      marginBottom: 10,
      marginTop: 16
    },
    heading3: {
      fontSize: Math.round(fontSize * 1.1),
      fontFamily: getBoldFont(fontFamily),
      marginBottom: 8,
      marginTop: 14
    },
    paragraph: {
      marginBottom: 10,
      marginTop: 10,
      textAlign: 'justify'
    },    list: {
      marginLeft: 20,
      marginBottom: 12,
      marginTop: 8
    },
    listItem: {
      flexDirection: 'row',
      marginBottom: 4,
      marginTop: 2,
      alignItems: 'flex-start',
      breakInside: 'avoid', // Prevent breaking inside list items
      orphans: 2, // Keep at least 2 lines together
      widows: 2 // Keep at least 2 lines together
    },
    listBullet: {
      width: 20,
      fontFamily: getBoldFont(fontFamily),
      marginRight: 8,
      textAlign: 'left',
      flexShrink: 0 // Prevent bullet from shrinking
    },
    listText: {
      flex: 1,
      flexShrink: 1, // Allow text to shrink if needed
      minHeight: lineHeight // Ensure minimum height for proper spacing
    },
    table: {
      marginVertical: 10,
      borderWidth: 1,
      borderColor: '#cccccc'
    },
    tableRow: {
      flexDirection: 'row',
      borderBottomWidth: 1,
      borderBottomColor: '#cccccc'
    },
    tableHeader: {
      backgroundColor: '#f5f5f5',
      fontFamily: getBoldFont(fontFamily)
    },
    tableCell: {
      flex: 1,
      padding: 8,
      borderRightWidth: 1,
      borderRightColor: '#cccccc'
    },
    code: {
      fontFamily: 'Courier',
      backgroundColor: '#f5f5f5',
      padding: 2,
      fontSize: fontSize * 0.9
    },
    codeBlock: {
      fontFamily: 'Courier',
      backgroundColor: '#f5f5f5',
      padding: 8,
      marginVertical: 8,
      fontSize: fontSize * 0.9,
      borderRadius: 4
    },
    blockquote: {
      borderLeftWidth: 4,
      borderLeftColor: '#cccccc',
      paddingLeft: 12,
      marginVertical: 8,
      fontStyle: 'italic'
    },
    bold: {
      fontFamily: getBoldFont(fontFamily)
    },
    italic: {
      fontFamily: getItalicFont(fontFamily)
    },
    mermaidDiagram: {
      marginVertical: 10,
      alignSelf: 'center',
      maxWidth: '80%'
    },
    mermaidError: {
      border: '2px solid #ff6b6b',
      borderRadius: 8,
      padding: 16,
      margin: 8,
      backgroundColor: '#fff5f5',
      color: '#c92a2a'
    }
  });
};

// Render inline markdown (bold, italic, code)
const renderInlineMarkdown = (text: string, styles: any, key = 0) => {
  const tokens: React.ReactNode[] = [];
  let working = text;
  let tokenKey = 0;

  // Handle bold+italic (***text*** or ___text___)
  const boldItalicRegex = /(\*\*\*|___)(.*?)\1/;
  const boldRegex = /(\*\*|__)(.*?)\1/;
  const italicRegex = /(\*|_)(.*?)\1/;
  const codeRegex = /`([^`]+)`/;

  while (working.length > 0) {
    let match;
    
    // Bold+italic
    match = boldItalicRegex.exec(working);
    if (match) {
      if (match.index! > 0) {
        tokens.push(<Text key={`${key}-${tokenKey++}`}>{working.slice(0, match.index)}</Text>);
      }
      tokens.push(
        <Text key={`${key}-${tokenKey++}`} style={[styles.bold, styles.italic]}>
          {match[2]}
        </Text>
      );
      working = working.slice(match.index! + match[0].length);
      continue;
    }
    
    // Bold
    match = boldRegex.exec(working);
    if (match) {
      if (match.index! > 0) {
        tokens.push(<Text key={`${key}-${tokenKey++}`}>{working.slice(0, match.index)}</Text>);
      }
      tokens.push(
        <Text key={`${key}-${tokenKey++}`} style={styles.bold}>
          {match[2]}
        </Text>
      );
      working = working.slice(match.index! + match[0].length);
      continue;
    }
    
    // Italic
    match = italicRegex.exec(working);
    if (match) {
      if (match.index! > 0) {
        tokens.push(<Text key={`${key}-${tokenKey++}`}>{working.slice(0, match.index)}</Text>);
      }
      tokens.push(
        <Text key={`${key}-${tokenKey++}`} style={styles.italic}>
          {match[2]}
        </Text>
      );
      working = working.slice(match.index! + match[0].length);
      continue;
    }
    
    // Inline code
    match = codeRegex.exec(working);
    if (match) {
      if (match.index! > 0) {
        tokens.push(<Text key={`${key}-${tokenKey++}`}>{working.slice(0, match.index)}</Text>);
      }
      tokens.push(
        <Text key={`${key}-${tokenKey++}`} style={styles.code}>
          {match[1]}
        </Text>
      );
      working = working.slice(match.index! + match[0].length);
      continue;
    }
    
    // No more markdown
    tokens.push(<Text key={`${key}-${tokenKey++}`}>{working}</Text>);
    break;
  }
  
  return tokens;
};

// Helper to process variable substitutions in templates
const processTemplate = (template: string, data: { title?: string }) => {
  if (!template) return '';
  return template
    .replace(/\[Document Title\]/g, data.title || 'Untitled Document')
    .replace(/\[Current Date\]/g, new Date().toLocaleDateString())
    .replace(/\[Current Year\]/g, new Date().getFullYear().toString());
};

const MarkdownRendererToPdf: React.FC<MarkdownRendererToPdfProps> = ({
  markdown,
  styleSettings = {},
  title,
  headerTemplate,
  footerTemplate,
  disableMermaid = false
}) => {
  const styles = React.useMemo(() => createStyleSheet(styleSettings), [styleSettings]);
  
  // Render content synchronously without useEffect to avoid loops
  const renderedContent = React.useMemo(() => {
    if (!markdown) return [];

    // For now, let's skip async Mermaid rendering in PDF to prevent loops
    // We'll render a simple version without Mermaid
    const mermaidContentMap = new Map<string, string>();
    const markdownInstance = createMarkdownInstance(mermaidContentMap);
    const tokens = markdownInstance.parse(markdown, {});
    const elements: React.ReactNode[] = [];
      let elementKey = 0;

    tokens.forEach((token, idx) => {
      switch (token.type) {
        case 'heading_open':
          const level = parseInt(token.tag.slice(1));
          const nextToken = tokens[idx + 1];
          if (nextToken && nextToken.type === 'inline') {
            let headingStyle = styles.heading1;
            if (level === 2) headingStyle = styles.heading2;
            else if (level === 3) headingStyle = styles.heading3;
            
            elements.push(
              <Text key={`heading-${elementKey++}`} style={headingStyle}>
                {renderInlineMarkdown(nextToken.content, styles, elementKey)}
              </Text>
            );
          }
          break;
            case 'paragraph_open':
          const nextPToken = tokens[idx + 1];
          if (nextPToken && nextPToken.type === 'inline') {
            // Debug: Check what's happening with Composite Document
            if (nextPToken.content && nextPToken.content.includes('Composite Document')) {
              console.log('Found Composite Document content:', nextPToken.content);
              console.log('Heading regex test:', /^(#{1,6})\s+(.+)$/.test(nextPToken.content.trim()));
            }
            
            // Check if this is actually a heading that wasn't parsed properly
            const headingMatch = /^(#{1,6})\s+(.+)$/.exec(nextPToken.content.trim());
            if (headingMatch) {
              const level = headingMatch[1].length;
              let headingStyle = styles.heading1;
              if (level === 2) headingStyle = styles.heading2;
              else if (level === 3) headingStyle = styles.heading3;
              else if (level >= 4) headingStyle = styles.heading3; // Use heading3 style for h4-h6
              
              elements.push(
                <Text key={`heading-${elementKey++}`} style={headingStyle}>
                  {renderInlineMarkdown(headingMatch[2], styles, elementKey)}
                </Text>
              );            } else {              // Check if this looks like a list item that wasn't parsed as one
              const listItemMatch = /^[\s]*[-*+]\s+(.+)$/.exec(nextPToken.content.trim());
              const orderedListMatch = /^[\s]*\d+\.\s+(.+)$/.exec(nextPToken.content.trim());
              
              if (listItemMatch) {                elements.push(
                  <View key={`listitem-${elementKey++}`} style={styles.listItem} wrap={false}>
                    <Text style={styles.listBullet}>• </Text>
                    <Text style={styles.listText}>
                      {renderInlineMarkdown(listItemMatch[1], styles, elementKey)}
                    </Text>
                  </View>
                );
              } else if (orderedListMatch) {
                elements.push(
                  <View key={`listitem-${elementKey++}`} style={styles.listItem} wrap={false}>
                    <Text style={styles.listBullet}>1. </Text>
                    <Text style={styles.listText}>
                      {renderInlineMarkdown(orderedListMatch[1], styles, elementKey)}
                    </Text>
                  </View>
                );
              } else {                // Check for Mermaid diagrams and replace with placeholder
                const mermaidRegex = /```mermaid\s+([\s\S]*?)\s+```/;
                const mermaidMatch = mermaidRegex.exec(nextPToken.content);
                if (mermaidMatch) {
                  const mermaidContent = mermaidMatch[1].trim();
                  const diagramType = mermaidContent.split('\n')[0].trim(); // Get first line to determine diagram type
                  
                  elements.push(
                    <View key={`mermaid-placeholder-${elementKey++}`} style={styles.mermaidError}>
                      <Text style={{ fontFamily: 'Helvetica-Bold', marginBottom: 4 }}>
                        Mermaid Diagram ({diagramType || 'diagram'})
                      </Text>
                      <Text style={{ fontSize: 10, color: '#666' }}>
                        {disableMermaid 
                          ? 'Mermaid diagrams are disabled in PDF export. Use the printable view for full diagram rendering.'
                          : 'Mermaid diagrams will be rendered in the printable view. PDF export shows placeholders only.'
                        }
                      </Text>
                      {!disableMermaid && (
                        <Text style={{ fontSize: 8, color: '#999', marginTop: 4, fontFamily: 'Courier' }}>
                          Content: {mermaidContent.substring(0, 50)}{mermaidContent.length > 50 ? '...' : ''}
                        </Text>
                      )}
                    </View>
                  );
                } else {
                  elements.push(
                    <Text key={`paragraph-${elementKey++}`} style={styles.paragraph}>
                      {renderInlineMarkdown(nextPToken.content, styles, elementKey)}
                    </Text>
                  );
                }
              }
            }
          }
          break;
          
        case 'code_block':
          elements.push(
            <Text key={`code-${elementKey++}`} style={styles.codeBlock}>
              {token.content}
            </Text>
          );
          break;

        case 'bullet_list_open':
          // Look ahead to collect all list items
          const bulletListItems: React.ReactNode[] = [];
          let bulletListIdx = idx + 1;
          
          while (bulletListIdx < tokens.length && tokens[bulletListIdx].type !== 'bullet_list_close') {
            const listToken = tokens[bulletListIdx];
            if (listToken.type === 'list_item_open') {
              // Find the content of this list item (skip list_item_open and paragraph_open)
              let contentIdx = bulletListIdx + 1;
              while (contentIdx < tokens.length && tokens[contentIdx].type !== 'paragraph_open') {
                contentIdx++;
              }
              contentIdx++; // Skip paragraph_open
              
              if (contentIdx < tokens.length && tokens[contentIdx].type === 'inline') {                bulletListItems.push(
                  <View key={`listitem-${elementKey++}`} style={styles.listItem} wrap={false}>
                    <Text style={styles.listBullet}>• </Text>
                    <Text style={styles.listText}>
                      {renderInlineMarkdown(tokens[contentIdx].content, styles, elementKey)}
                    </Text>
                  </View>
                );
              }
            }
            bulletListIdx++;
          }
            if (bulletListItems.length > 0) {
            elements.push(
              <View key={`list-${elementKey++}`} style={styles.list} wrap={false}>
                {bulletListItems}
              </View>
            );
          }
          break;

        case 'ordered_list_open':
          // Look ahead to collect all ordered list items
          const orderedListItems: React.ReactNode[] = [];
          let orderedListIdx = idx + 1;
          let itemNumber = 1;
          
          while (orderedListIdx < tokens.length && tokens[orderedListIdx].type !== 'ordered_list_close') {
            const listToken = tokens[orderedListIdx];
            if (listToken.type === 'list_item_open') {
              // Find the content of this list item
              let contentIdx = orderedListIdx + 1;
              while (contentIdx < tokens.length && tokens[contentIdx].type !== 'paragraph_open') {
                contentIdx++;
              }
              contentIdx++; // Skip paragraph_open
              
              if (contentIdx < tokens.length && tokens[contentIdx].type === 'inline') {                orderedListItems.push(
                  <View key={`orderitem-${elementKey++}`} style={styles.listItem} wrap={false}>
                    <Text style={styles.listBullet}>{itemNumber}. </Text>
                    <Text style={styles.listText}>
                      {renderInlineMarkdown(tokens[contentIdx].content, styles, elementKey)}
                    </Text>
                  </View>
                );
                itemNumber++;
              }
            }
            orderedListIdx++;
          }
            if (orderedListItems.length > 0) {
            elements.push(
              <View key={`ordered-list-${elementKey++}`} style={styles.list} wrap={false}>
                {orderedListItems}
              </View>
            );
          }
          break;

        case 'blockquote_open':
          // Look ahead to find the blockquote content
          let quoteIdx = idx + 1;
          while (quoteIdx < tokens.length && tokens[quoteIdx].type !== 'paragraph_open') {
            quoteIdx++;
          }
          quoteIdx++; // Skip paragraph_open
          
          if (quoteIdx < tokens.length && tokens[quoteIdx].type === 'inline') {
            elements.push(
              <Text key={`quote-${elementKey++}`} style={styles.blockquote}>
                {renderInlineMarkdown(tokens[quoteIdx].content, styles, elementKey)}
              </Text>
            );
          }
          break;
          
        default:
          // Handle other token types as needed
          break;
      }
    });

    return elements;
  }, [markdown, styles, disableMermaid]);

  return (
    <Document>
      <Page
        size={styleSettings?.pageSize as any || 'A4'}
        orientation={styleSettings?.orientation as any || 'portrait'}
        style={styles.page}
      >        {/* Header */}
        {headerTemplate && headerTemplate.trim() !== "" && (
          <View style={styles.header} fixed>
            {(() => {
              const processed = processTemplate(headerTemplate, { title });
              
              // Check if this is a heading
              const headingMatch = /^(#{1,6})\s+(.+)$/.exec(processed.trim());
              if (headingMatch) {
                // Render as heading with proper style
                return (
                  <Text style={styles.heading1}>
                    {renderInlineMarkdown(headingMatch[2], styles)}
                  </Text>
                );
              }
              
              // Not a heading, render as regular text with inline markdown
              return (
                <Text>
                  {renderInlineMarkdown(processed, styles)}
                </Text>
              );
            })()}
          </View>
        )}

        {/* Main content */}
        <View style={styles.section}>
          {renderedContent.map((element, index) => (
           <React.Fragment key={index}>{element}</React.Fragment>
          ))}
        </View>        {/* Footer */}
        {footerTemplate && footerTemplate.trim() !== "" && (
          <View style={styles.footer} fixed>
            <Text
              render={({ pageNumber, totalPages }) => {
                const processed = processTemplate(footerTemplate, { title })
                  .replace(/\[Page Number\]/g, pageNumber ? pageNumber.toString() : '?')
                  .replace(/\[Total Pages\]/g, totalPages ? totalPages.toString() : '?');
                
                // Check if this is a heading in the footer
                const headingMatch = /^(#{1,6})\s+(.+)$/.exec(processed.trim());
                if (headingMatch) {
                  // For headings in footer, just return the text without # symbols
                  return renderInlineMarkdown(headingMatch[2], styles);
                }
                
                return renderInlineMarkdown(processed, styles);
              }}
            />
          </View>
        )}
      </Page>
    </Document>
  );
};

export default memo(MarkdownRendererToPdf);
